import { useEffect, useRef, useState } from "react";
import { Loader2, MessageSquareText, ShieldCheck, Smartphone } from "lucide-react";
import { toast } from "sonner";
import api from "../api";
import { useLanguage } from "../i18n";
import PhoneInput from "./PhoneInput";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";

const TD_KEY = "simamma_td";
const hasTrustedDevice = () => !!localStorage.getItem(TD_KEY);

/** Etichetta leggibile del dispositivo corrente (es. "Chrome · Android"). */
const deviceLabel = () => {
  const ua = navigator.userAgent || "";
  const os = /Windows/i.test(ua) ? "Windows" : /Android/i.test(ua) ? "Android"
    : /iPhone|iPad/i.test(ua) ? "iOS" : /Mac OS/i.test(ua) ? "macOS" : /Linux/i.test(ua) ? "Linux" : "Web";
  const br = /Edg\//i.test(ua) ? "Edge" : /OPR\//i.test(ua) ? "Opera" : /Chrome\//i.test(ua) ? "Chrome"
    : /Firefox\//i.test(ua) ? "Firefox" : /Safari\//i.test(ua) ? "Safari" : "Browser";
  return `${br} · ${os}`;
};

/** Gate telefono pre-pagamento. mode="certify": certifica il numero (prima volta);
 * mode="confirm": SMS di conferma ordine sul numero già verificato (a ogni acquisto).
 * Con la spunta "ricorda" il dispositivo diventa affidabile e salta l'SMS per 30 giorni. */
export default function PhoneGateModal({ open, onOpenChange, mode, onDone }) {
  const { t } = useLanguage();
  const [step, setStep] = useState("phone"); // certify: phone→code · confirm: solo code
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [remember, setRemember] = useState(false);
  const [busy, setBusy] = useState(false);
  const sentRef = useRef(false);

  useEffect(() => {
    if (!open) { setRemember(false); sentRef.current = false; return; }
    // All'APERTURA il passo dipende dalla modalità richiesta: certify chiede prima il
    // numero, confirm solo il codice. (Prima il reset avveniva alla chiusura, quando
    // mode è già tornata a "confirm" di default: certify si apriva sul passo codice.)
    setStep(mode === "confirm" ? "code" : "phone");
    setCode("");
    // confirm: invio automatico del codice all'apertura (una sola volta per apertura)
    if (mode === "confirm" && !sentRef.current) {
      sentRef.current = true;
      api.post("/phone/send-order-code").catch((e) => {
        const d = e.response?.data?.detail || "";
        toast.error(d === "too_many_attempts" ? t("rec.proofLocked") : d === "phone_trial_unverified" ? t("acc.phoneFailed") : t("misc.error"));
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode]);

  const inputCls = "w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none focus:border-[#FF6B35] transition-colors";
  const btnCls = "w-full rounded-full bg-[#FF6B35] text-white px-6 py-3 min-h-[44px] font-mono-t text-xs font-bold uppercase tracking-[0.15em] hover:bg-[#E8551F] transition-colors disabled:opacity-60 flex items-center justify-center gap-2";

  const errToast = (e) => {
    const d = e.response?.data?.detail || "";
    if (d === "invalid_code") toast.error(t("pg.wrong"));
    else if (d === "too_many_attempts") toast.error(t("rec.proofLocked"));
    else if (d === "phone_trial_unverified") toast.error(t("acc.phoneFailed"));
    else if (d === "invalid_phone" || d === "send_failed") toast.error(t("acc.phoneInvalid"));
    else toast.error(d || t("misc.error"));
  };

  const sendCertify = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try {
      await api.post("/phone/send", { phone });
      toast.success(t("pg.sentTo"));
      setStep("code");
    } catch (e2) { errToast(e2); } finally { setBusy(false); }
  };

  const verify = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try {
      if (mode === "certify") await api.post("/phone/verify", { phone, code });
      else await api.post("/phone/verify-order-code", { code });
      // dispositivo affidabile: subito dopo l'SMS riuscito (prova di possesso del telefono)
      if (remember && !hasTrustedDevice()) {
        try {
          const { data } = await api.post("/phone/trust-device", { device_name: deviceLabel() });
          localStorage.setItem(TD_KEY, data.token);
          toast.success(t("pg.rememberDone"));
        } catch { /* la fiducia è un extra: non blocca mai il pagamento */ }
      }
      toast.success(t("pg.done"));
      onDone?.();
    } catch (e2) { errToast(e2); } finally { setBusy(false); }
  };

  const resend = async () => {
    setBusy(true);
    try {
      if (mode === "certify") await api.post("/phone/send", { phone });
      else await api.post("/phone/send-order-code");
      toast.success(t("pg.sentTo"));
    } catch (e2) { errToast(e2); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="phone-gate-dialog" aria-describedby="phone-gate-desc" className="rounded-3xl sm:max-w-sm">
        <DialogHeader>
          <DialogTitle className="font-display text-xl text-slate-900 flex items-center gap-2">
            {mode === "certify" ? <Smartphone className="w-5 h-5 text-[#FF6B35]" /> : <MessageSquareText className="w-5 h-5 text-[#FF6B35]" />}
            {t(mode === "certify" ? "pg.certifyTitle" : "pg.confirmTitle")}
          </DialogTitle>
          <p id="phone-gate-desc" className="text-sm text-slate-500">
            {t(mode === "certify" ? "pg.certifySub" : "pg.confirmSub")}
          </p>
        </DialogHeader>

        {step === "phone" ? (
          <form onSubmit={sendCertify} className="space-y-3 pt-1">
            <PhoneInput testid="phone-gate-phone" required onChange={setPhone}
              className="w-full rounded-2xl border border-slate-200 bg-white focus-within:border-[#FF6B35] transition-colors" />
            <button data-testid="phone-gate-send" type="submit" disabled={busy || phone.length < 8} className={btnCls}>
              {busy && <Loader2 className="w-4 h-4 animate-spin" />} {t("pg.send")}
            </button>
          </form>
        ) : (
          <form onSubmit={verify} className="space-y-3 pt-1">
            <input data-testid="phone-gate-code" required value={code} onChange={(e) => setCode(e.target.value)}
              placeholder={t("pg.codeLabel")} autoComplete="one-time-code" inputMode="numeric"
              className={`${inputCls} text-center text-lg tracking-[0.4em] font-mono-t`} />
            {!hasTrustedDevice() && (
              <label data-testid="phone-gate-remember" className="flex items-start gap-2.5 cursor-pointer select-none">
                <input data-testid="phone-gate-remember-check" type="checkbox" checked={remember}
                  onChange={(e) => setRemember(e.target.checked)} className="mt-0.5 w-4 h-4 accent-[#FF6B35]" />
                <span className="text-xs text-slate-500 leading-snug flex items-start gap-1.5">
                  <ShieldCheck className="w-3.5 h-3.5 mt-px shrink-0 text-[#FF6B35]" />{t("pg.remember")}
                </span>
              </label>
            )}
            <button data-testid="phone-gate-verify" type="submit" disabled={busy || code.trim().length < 4} className={btnCls}>
              {busy && <Loader2 className="w-4 h-4 animate-spin" />} {t("pg.verify")}
            </button>
            <button type="button" data-testid="phone-gate-resend" onClick={resend} disabled={busy}
              className="w-full text-center font-mono-t text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400 hover:text-[#FF6B35] transition-colors">
              {t("pg.resend")}
            </button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
